/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Toast from 'react-native-toast-message';
import { View, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { FontAwesome, FontAwesome5 } from '@expo/vector-icons';
import { buttonFloating } from '@/assets/styles/buttonStyles';
import SociollaText from '@components/sociolla/SociollaText';
import TitleSubSection from '@components/global/TitleSubSection';
import Colors from '@global/colors';
import Button from '@components/form/Button';
import FileInput from '@components/form/FileInput';
import Input from '@components/form/Input';
import AccountGroupSearch from '@components/form/search/AccountGroupSearch';
import Table from '@components/table/Table';
import * as Layout from '@components/screen/Layout';
import { isWeb } from '@global/screens';
import { showPopup } from '@/store/modal';

import useApi from '@/hooks/useApi';
import { MsB2b } from '@api/ms-b2b';

const tableStructure = {
  name: {
    head: 'Planogram Name',
    body: 'planogram_name',
    type: 'slot',
  },
  url: {
    head: 'File Name',
    body: 'url',
    type: 'slot',
  },
  action: {
    head: 'Action',
    body: 'action',
    type: 'slot',
  },
};

const PlanogramFormScreen = (props) => {
  const dispatch = useDispatch();

  const [detailData, setDetailData] = useState({});
  const [isEdit, setIsEdit] = useState(false);

  const FormValidationSchema = Yup.object().shape({
    account_group: Yup.object()
      .required('Account Group is required.')
      .test(
        'account_group-required',
        'account group is required.',
        (val) => val && Object.keys(val).length,
      ),
  });

  const getPlanogram = useApi(MsB2b.getPlanogram);
  const getPlanogramDetail = useApi(MsB2b.getPlanogramDetail);

  const addShelving = (setFieldValue, shelves) => {
    const newShelves = [
      ...shelves,
      {
        name: shelves.name ? shelves.name : 'Planogram Name',
        position: shelves.length + 1,
        url: '',
      },
    ];
    setFieldValue('shelves', newShelves);
  };

  const convertData = (data) => {
    const count = 1;
    const result = [];
    const existingCount = data ? data.length : 0;

    if (data && data.length) {
      data.forEach((d) => {
        result.push({
          name: d.name,
          position: d.position,
          url: d.url,
        });
      });
    }

    for (let i = existingCount; i < count; i++) {
      result.push({
        name: '',
        position: i + 1,
        url: '',
      });
    }

    return result;
  };

  const getDetail = async (id) => {
    const response = await getPlanogramDetail.request(id);
    if (response?.data?.data && Object.keys(response.data.data).length) {
      const result = response.data.data;
      if (result.shelves && result.shelves.length) {
        result.shelves = convertData(result.shelves);
      } else {
        result.shelves = convertData([]);
      }
      setDetailData(result);
    }
  };

  const onAction = async (item, type, index, values, setFieldValue) => {
    if (type == 'hapus') {
      const deleteItem = values.shelves.findIndex((shelf) => shelf === item);
      if (deleteItem !== -1) {
        const updatedShelves = values.shelves.filter(
          (shelf, index) => index !== deleteItem,
        );
        setFieldValue('shelves', updatedShelves);
      }
    } else {
      const tempData = [...values.shelves];
      tempData[index].url = '';
      setFieldValue('shelves', tempData);
    }
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setIsEdit(false);
      setDetailData({});
      if (props?.route?.params?.id) {
        setIsEdit(true);
        getDetail(props.route.params.id);
      }
    }
    return () => {
      mounted = false;
    };
  }, [props]);

  return (
    <Layout.Container>
      {isEdit && getPlanogramDetail && getPlanogramDetail.loading ? (
        <Layout.Wrapper>
          <Layout.Loader />
        </Layout.Wrapper>
      ) : (isEdit ? detailData && Object.keys(detailData).length : true) ? (
        <Formik
          initialValues={{
            account_group:
              isEdit &&
              detailData &&
              detailData.account_group &&
              Object.keys(detailData.account_group).length
                ? detailData.account_group
                : '',
            shelves:
              isEdit &&
              detailData &&
              detailData.shelves &&
              detailData.shelves.length
                ? detailData.shelves
                : convertData([]),
          }}
          validationSchema={FormValidationSchema}
          onSubmit={async (payload) => {
            dispatch(showPopup({ name: 'isLoading', value: true }));
            try {
              let isExists = false;
              if (
                isEdit
                  ? detailData?.account_group?.id != payload?.account_group?.id
                  : true
              ) {
                const response = await getPlanogram.request({
                  params: {
                    filter: { 'account_group.id': payload.account_group.id },
                    limit: 1,
                  },
                });
                if (response?.data?.data?.length) {
                  isExists = true;
                }
              }

              if (isExists) {
                dispatch(showPopup({ name: 'isLoading', value: false }));
                Toast.show({
                  text1: 'Planogram for this account group is already exist',
                  type: 'error',
                });
              } else {
                if (payload) {
                  if (
                    payload.account_group &&
                    Object.keys(payload.account_group).length
                  ) {
                    payload.account_group = {
                      id: payload.account_group.id,
                      code: payload.account_group.code,
                    };
                  }
                  if (payload.shelves && payload.shelves.length) {
                    payload.shelves = payload.shelves.filter((res) => res.url);
                  } else {
                    payload.shelves = [];
                  }
                }

                if (isEdit) {
                  const updateData = await MsB2b.updatePlanogram(
                    detailData._id,
                    payload,
                  );
                  if (updateData && updateData.data && updateData.data.data) {
                    dispatch(showPopup({ name: 'isLoading', value: false }));
                    Toast.show({
                      text1: 'Successfully edited data',
                      type: 'success',
                    });
                    props.navigation.navigate('PlanogramDetailScreen', {
                      titleScreen: detailData.account_group.code,
                      name: detailData.account_group.code,
                      id: detailData._id,
                    });
                  } else {
                    dispatch(showPopup({ name: 'isLoading', value: false }));
                    Toast.show({
                      text1: 'A system error occurred',
                      type: 'error',
                    });
                  }
                } else {
                  const createData = await MsB2b.createPlanogram(payload);
                  if (createData && createData.data && createData.data.data) {
                    const result = createData.data.data;
                    dispatch(showPopup({ name: 'isLoading', value: false }));
                    Toast.show({
                      text1: 'Successfully added data',
                      type: 'success',
                    });
                    if (result._id) {
                      props.navigation.navigate('PlanogramDetailScreen', {
                        titleScreen: result.account_group.code,
                        name: result.account_group.code,
                        id: result._id,
                      });
                    } else {
                      props.navigation.navigate('PlanogramScreen');
                    }
                  } else {
                    dispatch(showPopup({ name: 'isLoading', value: false }));
                    Toast.show({
                      text1: 'A system error occurred',
                      type: 'error',
                    });
                  }
                }
              }
            } catch (error) {
              dispatch(showPopup({ name: 'isLoading', value: false }));
              Toast.show({
                text1:
                  (error &&
                    error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                  'A system error occurred',
                type: 'error',
              });
            }
          }}>
          {({ setFieldValue, handleSubmit, errors, touched, values }) => (
            <>
              {isWeb ? (
                <Layout.Wrapper white={false}>
                  <Layout.Back
                    title="Planogram"
                    onCallback={() =>
                      isEdit
                        ? props.navigation.navigate(
                            'PlanogramDetailScreen',
                            props.route.params,
                          )
                        : props.navigation.navigate('PlanogramScreen')
                    }
                  />
                  <Layout.Title
                    title={isEdit ? 'Edit Planogram' : 'Add Planogram'}>
                    <Button
                      rounded={true}
                      buttonStyle={{ width: 120 }}
                      title={
                        <SociollaText
                          type="bold"
                          style={{
                            fontSize: 14,
                            letterSpacing: 0.5,
                            color: Colors.white,
                          }}>
                          {isEdit ? 'UPDATE' : 'SAVE'}
                        </SociollaText>
                      }
                      onPress={handleSubmit}
                    />
                  </Layout.Title>
                </Layout.Wrapper>
              ) : (
                false
              )}
              <Layout.Wrapper rounded>
                <ScrollView>
                  <Layout.FormWrapper
                    style={{
                      width: isWeb ? 400 : '100%',
                      paddingHorizontal: 15,
                      paddingTop: 20,
                      ...(isWeb
                        ? {
                            paddingHorizontal: 20,
                            marginBottom: 15,
                          }
                        : {}),
                    }}>
                    <Layout.FormGroup>
                      <AccountGroupSearch
                        value={values.account_group}
                        isRequired={true}
                        hasError={
                          errors.account_group && touched.account_group
                            ? errors.account_group
                            : null
                        }
                        onChange={(value) =>
                          setFieldValue('account_group', value)
                        }
                      />
                    </Layout.FormGroup>
                  </Layout.FormWrapper>
                  <TitleSubSection
                    title="PLANOGRAM"
                    button={{
                      label: 'Add Planogram',
                      callback: () =>
                        addShelving(setFieldValue, values.shelves),
                    }}
                  />
                  <Layout.FormWrapper
                    style={{
                      paddingVertical: isWeb ? 10 : 0,
                      paddingHorizontal: isWeb ? 20 : 0,
                    }}>
                    {isWeb ? (
                      <Table
                        structures={tableStructure}
                        data={values.shelves}
                        withFetch={false}
                        hasNoHide={true}
                        showFilter={false}
                        noTopSection={true}
                        showPagination={true}
                        withoutShadow={true}
                        totalData={values.shelves.length || 0}
                        limit={values.shelves.length || 7}
                        slot={{
                          planogram_name: ({ data, index }) => (
                            <View
                              style={{ display: 'table-caption' }}
                              key={index}>
                              <Input
                                value={data.name}
                                isRequired
                                placeholder="Planogram name"
                                onChangeText={(text) => {
                                  const tempData = [...values.shelves];
                                  tempData[index] = {
                                    ...tempData[index],
                                    name: text,
                                  };
                                  setFieldValue('shelves', tempData);
                                }}
                              />
                            </View>
                          ),
                          url: ({ data, index }) => (
                            <View
                              style={{ display: 'table-caption' }}
                              key={index}>
                              {!data.url ? (
                                <FileInput
                                  showTrash={false}
                                  value={data.url}
                                  onChange={(value) => {
                                    const tempData = JSON.parse(
                                      JSON.stringify(values.shelves),
                                    );
                                    tempData[index].url = value;
                                    setFieldValue('shelves', tempData);
                                  }}
                                />
                              ) : (
                                <SociollaText>{data.url}</SociollaText>
                              )}
                            </View>
                          ),
                          action: ({ data, index }) => (
                            <View style={styles.action} key={`action-${index}`}>
                              <TouchableOpacity
                                activeOpacity={0.8}
                                style={{ marginRight: 10 }}
                                onPress={() =>
                                  onAction(
                                    data,
                                    'edit',
                                    index,
                                    values,
                                    setFieldValue,
                                  )
                                }>
                                <FontAwesome
                                  name="pencil-square-o"
                                  size={15}
                                  color="black"
                                />
                              </TouchableOpacity>
                              <TouchableOpacity
                                activeOpacity={0.8}
                                onPress={() =>
                                  onAction(
                                    data,
                                    'hapus',
                                    index,
                                    values,
                                    setFieldValue,
                                  )
                                }>
                                <FontAwesome5
                                  name="trash"
                                  size={15}
                                  color="black"
                                />
                              </TouchableOpacity>
                            </View>
                          ),
                        }}
                      />
                    ) : (
                      values.shelves.map((item, index) => (
                        <View style={styles.mobilePlanoList} key={index}>
                          <Layout.FormGroup key={index}>
                            <Input
                              label="Planogram Name"
                              value={item.name}
                              placeholder="Enter Planogram name"
                              hasError={
                                errors.shelves &&
                                errors.shelves[index] &&
                                touched.shelves &&
                                touched.shelves[index]
                                  ? errors.shelves[index].name
                                  : null
                              }
                              onChangeText={(text) => {
                                const tempData = JSON.parse(
                                  JSON.stringify(values.shelves),
                                );
                                tempData[index].name = text;
                                setFieldValue('shelves', tempData);
                              }}
                            />
                          </Layout.FormGroup>
                          <Layout.FormGroup>
                            {!item.url ? (
                              <FileInput
                                label={item.name ? item.name : 'Planogram Name'}
                                value={item.url}
                                onChange={(value) => {
                                  const tempData = JSON.parse(
                                    JSON.stringify(values.shelves),
                                  );
                                  tempData[index].url = value;
                                  setFieldValue('shelves', tempData);
                                }}
                              />
                            ) : (
                              <View>
                                <SociollaText
                                  style={{
                                    fontSize: 11,
                                    marginBottom: 8,
                                  }}>
                                  {item.name
                                    ? item.name
                                    : 'Planogram File Name'}
                                </SociollaText>
                                <SociollaText>{item.url}</SociollaText>
                                <TouchableOpacity
                                  activeOpacity={0.8}
                                  onPress={() =>
                                    onAction(
                                      item,
                                      'hapus',
                                      index,
                                      values,
                                      setFieldValue,
                                    )
                                  }>
                                  <Button
                                    rounded={true}
                                    buttonStyle={{ width: '20%', marginTop: 8 }}
                                    title={
                                      <SociollaText
                                        type="bold"
                                        style={{
                                          fontSize: 12,
                                          letterSpacing: 0.5,
                                          color: Colors.white,
                                        }}>
                                        Delete
                                      </SociollaText>
                                    }
                                  />
                                </TouchableOpacity>
                              </View>
                            )}
                          </Layout.FormGroup>
                        </View>
                      ))
                    )}
                  </Layout.FormWrapper>
                </ScrollView>
              </Layout.Wrapper>
              {!isWeb ? (
                <View style={buttonFloating}>
                  <Button
                    rounded={true}
                    buttonStyle={{ width: '100%' }}
                    title={
                      <SociollaText
                        type="bold"
                        style={{
                          fontSize: 14,
                          letterSpacing: 0.5,
                          color: Colors.white,
                        }}>
                        {isEdit ? 'UPDATE' : 'SAVE'}
                      </SociollaText>
                    }
                    onPress={handleSubmit}
                  />
                </View>
              ) : (
                false
              )}
            </>
          )}
        </Formik>
      ) : (
        false
      )}
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  action: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  mobilePlanoList: {
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
    padding: 10,
    width: '100%',
  },
});

export default PlanogramFormScreen;
